import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation18({ animation, badge, all_animations }: Props) {
  const [showBadge, setShowBadge] = useState(false)

  useEffect(() => {
    setTimeout(() => setShowBadge(true), 200)
  }, [])

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes haloRotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes haloShine {
          0%, 100% {
            box-shadow: 0 0 60px 30px rgba(255, 241, 152, 0.3),
                        0 0 100px 60px rgba(255, 241, 152, 0.2),
                        0 0 140px 90px rgba(255, 241, 152, 0.1);
          }
          50% {
            box-shadow: 0 0 80px 40px rgba(255, 241, 152, 0.5),
                        0 0 120px 80px rgba(255, 241, 152, 0.3),
                        0 0 160px 110px rgba(255, 241, 152, 0.15);
          }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        .halo-rotate {
          animation: haloRotate 20s linear infinite;
        }
        .halo-shine {
          animation: haloShine 2s ease-in-out infinite;
        }
        .float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh] relative">
        {/* Halo tournant */}
        <div className={`absolute halo-rotate transition-opacity duration-500 ${showBadge ? 'opacity-100' : 'opacity-0'}`}>
          <div className="w-80 h-80 rounded-full border-4 border-dashed border-tessan-yellow/30" />
          <div className="absolute inset-4 rounded-full border-2 border-dotted border-tessan-cream/20" />
        </div>

        {/* Badge central */}
        <div className={`relative z-10 transition-all duration-700 ${showBadge ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}>
          <div className="float">
            {/* Halo glow */}
            <div className="absolute inset-0 rounded-2xl halo-shine bg-tessan-yellow/10" />

            {/* Badge */}
            <div className="relative w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl">
              <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
            </div>

            {/* Rayons lumineux */}
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute left-1/2 top-1/2 w-1 h-16 bg-gradient-to-t from-tessan-yellow/50 to-transparent origin-bottom"
                style={{
                  transform: `translateX(-50%) rotate(${i * 45}deg) translateY(-88px)`
                }}
              />
            ))}
          </div>
        </div>

        <div className={`mt-16 text-center transition-all duration-700 delay-500 ${showBadge ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow">{badge.name}</div>
          <div className="text-lg sm:text-xl text-tessan-cream mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
